<template>
  <div :class="wrapperClasses">
    <FormulateForm v-model="form" @submit="onSubmit">
      <div
        class="border-b border-truegray-200 pb-5 mb-12 flex flex-col sm:flex-row gap-4 justify-between"
      >
        <h3 class="mb-0">{{ submitText }}</h3>
        <div
          class="border border-truegray-200 rounded-md cursor-pointer px-2 py-1 flex justify-center items-center shadow"
          @click="() => showModal(null, 'co-info-modal')"
        >
          <div class="w-6 mr-2">
            <img
              src="/img/logo-cw.png"
              alt="CareerWise logo"
              class="rounded-sm w-6"
            />
          </div>
          <h4 class="text-sm">What is a Connection?</h4>
        </div>
      </div>
      <!-- Form content -->
      <div class="grid grid-cols-1 lg:grid-cols-2 lg:gap-8 p-4">
        <!-- Content column 1 -->
        <div class="col-span-1 lg:border-r lg:border-truegray-200 lg:pr-12">
          <div class="w-full mb-12">
            <h2 class="mb-2">Step 1.</h2>
            <p class="mb-0 text-truegray-500 italic">
              {{ stepLabel.step1 }}
            </p>
            <div
              class="w-full col-span-2 text-cw-alt hover:text-cw-500 transition-all flex text-xs font-bold w-full items-center justify-start cursor-pointer mt-2"
              @click="showExampleProfile"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                class="w-4 h-4 mr-4"
              >
                <path
                  fill-rule="evenodd"
                  d="M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0ZM9 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM6.75 8a.75.75 0 0 0 0 1.5h.75v1.75a.75.75 0 0 0 1.5 0v-2.5A.75.75 0 0 0 8.25 8h-1.5Z"
                  clip-rule="evenodd"
                />
              </svg>

              Show example profile
            </div>
          </div>

          <!-- Image selector -->
          <div class="w-full">
            <FormulateInput
              name="image"
              type="image"
              :label="formLabel.image"
              label-position="after"
              help="Image size must be less than 1 MB"
              class="profile-image"
              validation="maxImageSize"
              :validation-messages="{
                maxImageSize: 'Please select a valid image.',
              }"
              error-behavior="submit"
            />
          </div>

          <!-- General details -->
          <div
            class="grid grid-cols-2 gap-4 py-12 border-b border-truegray-200"
          >
            <div class="col-span-2 lg:col-span-1">
              <FormulateInput
                name="name"
                :label="formLabel.name"
                validation="required:trim"
              />
            </div>
            <div class="col-span-2 lg:col-span-1">
              <FormulateInput
                name="jobTitle"
                :label="formLabel.jobTitle"
                validation="required:trim"
                :validation-name="
                  isContributorAdminConnectionsOnly
                    ? 'Job title/company name'
                    : 'Job title'
                "
                :placeholder="
                  isContributorAdminConnectionsOnly
                    ? 'Job Title - Company Name'
                    : ''
                "
              />
            </div>
            <div v-if="!isContributorContext" class="col-span-2 lg:col-span-1">
              <FormulateInput name="company" :label="formLabel.company" />
            </div>
            <div v-if="isContributorContext" class="col-span-2">
              <FormulateInput
                name="regions"
                type="tags"
                :label="formLabel.region"
                validation="required"
                validation-name="At least one region"
                :options="tagsRegions"
                :count="form.regions?.length || 0"
                :is-collapsed="isTagsCollapsed['regions']"
              />
            </div>
            <div v-else class="col-span-2 lg:col-span-1">
              <FormulateInput
                name="region"
                type="select"
                :label="formLabel.region"
                :placeholder="formLabel.regionPlaceholder"
                validation="required"
                :options="tagsRegions"
              />
            </div>

            <div
              v-show="isManagerContext && isAdmin"
              class="col-span-2 lg:col-span-1"
            >
              <FormulateInput
                name="type"
                type="select"
                :label="formLabel.type"
                :placeholder="formLabel.typePlaceholder"
                validation="required"
                :options="connectionTypes"
              />
            </div>
            <!-- 
              @TODO Current logic from marketing team says that all non-contributor
              connections should not be public and all contributor connections should be.
              We are opting to leave the logic here should that choice ever change or until
              we are confident it is solidified. It defaults to FALSE which will make it 'correct'
              in all non-contributor contexts, and is hard set in the backend to TRUE for contributors.
            -->
            <!--
              <div v-if="!isContributorContext" class="col-span-2 lg:col-span-2">
              <div class="w-full lg:w-1/2">
              <FormulateInput
              v-model="publicSelectValue"
              name="public"
              type="select"
              :label="formLabel.public"
              validation="required"
              :options="publicContactOptions"
              :value="publicSelectValue"
              @input="publicSelectValue = $event"
              />
              </div>
              <p
              v-if="form.public"
              class="italic text-truegray-500 mt-2 text-sm"
              >
              A public connection can be seen on the school's CareerWise site
              and be contacted through it, you can optionally still hide your
              phone number from public view
              </p>
              <p v-else class="italic text-truegray-500 mt-2 text-sm">
              A private connection will only have their details shown to the
              school admins
              </p>
              </div> 
            
              <FormulateInput
              v-if="!isContributorContext"
              name="publicPhone"
              type="checkbox"
              label="Public phone number"
              />
            -->
            <div
              v-if="isManagerContext && isAdmin"
              class="col-span-1 flex items-center lg:pt-6 lg:pl-4"
            >
              <FormulateInput
                name="policeChecked"
                type="checkbox"
                :label="formLabel.policeChecked"
              />
            </div>
            <div
              v-if="isManagerContext && isAdmin && !isEditing"
              class="col-span-2"
            >
              <FormulateInput
                name="saveConnection"
                type="checkbox"
                :label="formLabel.saveConnection"
              />
            </div>
          </div>

          <!-- Contact details -->
          <div
            class="grid grid-cols-2 gap-4 py-12 border-b border-truegray-200 lg:border-0"
          >
            <div class="col-span-2 lg:col-span-1">
              <FormulateInput
                name="email"
                :label="formLabel.email"
                validation="bail|required:trim|email"
                :disabled="isContributorAdminConnectionsOnly"
              />
            </div>
            <div class="col-span-2 lg:col-span-1">
              <FormulateInput
                name="phone"
                :label="formLabel.phone"
                validation="required:trim|number|valid"
                validation-name="Phone number"
                :validation-rules="{
                  valid: ({ value }) => /^[0-9]+$/.test(value),
                }"
                :validation-messages="{
                  valid: 'Please enter a valid phone number.',
                }"
              />
            </div>
            <div class="col-span-2 lg:col-span-1">
              <FormulateInput name="linkedIn" :label="formLabel.linkedIn" />
            </div>
            <div v-if="isContributorContext" class="col-span-2">
              <FormulateInput
                name="publicPhone"
                type="checkbox"
                label="Public phone number"
              />
              <p class="italic text-truegray-500 mt-2 text-sm">
                We require a phone number for sign up, but you can optionally
                hide this from public view
              </p>
            </div>

            <div class="col-span-2 lg:col-span-2 w-full pt-12 pb-2">
              <h2 class="mb-2">Step 2.</h2>
              <p class="mb-0 text-truegray-500 italic">
                {{ stepLabel.step2 }}
              </p>
            </div>
            <div class="col-span-2 lg:col-span-2 w-full">
              <FormulateInput
                name="description"
                type="textarea"
                rows="8"
                :label="formLabel.description"
                validation="required:trim"
              />
            </div>
            <div
              class="w-full col-span-2 text-cw-alt transition-all hover:text-cw-500 flex text-xs font-bold w-full items-center justify-start cursor-pointer"
              @click="showExampleProfile"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                class="w-4 h-4 mr-4"
              >
                <path
                  fill-rule="evenodd"
                  d="M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0ZM9 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM6.75 8a.75.75 0 0 0 0 1.5h.75v1.75a.75.75 0 0 0 1.5 0v-2.5A.75.75 0 0 0 8.25 8h-1.5Z"
                  clip-rule="evenodd"
                />
              </svg>

              Show example profile
            </div>
          </div>
        </div>

        <!-- Content column 2 -->
        <div class="col-span-1 lg:pl-4">
          <div class="w-full border-b border-truegray-300 pb-12 mt-12 lg:mt-0">
            <FormulateInput
              name="industries"
              type="tags"
              :label="formLabel.industries"
              validation="required"
              validation-name="Industry selection"
              :options="tagsIndustries"
              :count="form.industries?.length || 0"
              :is-collapsed="isTagsCollapsed['industries']"
            />
          </div>

          <div class="w-full pt-12">
            <h2 class="mb-2">Step 3.</h2>
            <p class="mb-0 text-truegray-500 italic">
              {{ stepLabel.step3 }}
            </p>
          </div>

          <div class="w-full mt-12">
            <FormulateInput
              name="preferences"
              type="tags"
              :label="formLabel.preferences"
              validation-name="Preference selection"
              :options="tagsPreferences"
              :count="form.preferences?.length || 0"
              :is-collapsed="isTagsCollapsed['preferences']"
            />
          </div>
          <div class="w-full mt-12 lg:mt-6">
            <template v-if="!isContributorContext">
              <div class="w-full mb-2">
                <p>
                  {{ formLabel.supportTitle }} {{ settings.profile_title }} ?
                </p>
              </div>
              <div class="w-full">
                <FormulateInput
                  name="schoolSupport"
                  type="tags"
                  :label="formLabel.schoolSupport"
                  validation-name="School support selection"
                  :options="tagsSchoolSupport"
                  :count="form.schoolSupport?.length || 0"
                  :is-collapsed="isTagsCollapsed['schoolSupport']"
                />
              </div>
            </template>
            <template v-else>
              <div class="w-full">
                <FormulateInput
                  name="organisationsSupport"
                  type="tags"
                  :label="formLabel.organisationsSupport"
                  validation-name="School support selection"
                  :options="tagsOrganisations"
                  :count="form.organisationsSupport?.length || 0"
                  :is-collapsed="isTagsCollapsed['organisationsSupport']"
                />
              </div>
            </template>
          </div>
          <div
            v-if="!isEditing"
            class="w-full pt-12 mt-12 border-t border-truegray-200"
          >
            <div
              class="flex flex-col gap-4 sm:text-sm text-bluegray-700 focus:text-black active:text-black focus:outline-none active:outline-none transition-all duration-100 ease-in-out border border-bluegray-300 focus:border-black active:border-black rounded bg-white p-6 w-full"
            >
              <h5>Code of Conduct</h5>
              <p>
                {{ conductText }}
              </p>

              <div>
                <button
                  class="btn btn-trans-black mb-0"
                  @click="(e) => showModal(e, 'co-conduct-modal')"
                >
                  Open & Read
                </button>
              </div>

              <FormulateInput
                name="conduct"
                type="checkbox"
                :label="formLabel.conduct"
                validation="required"
                validation-name="Code of Conduct confirmation"
              />
            </div>
          </div>
          <div class="w-full pt-12 flex justify-center lg:justify-end">
            <FormulateInput
              type="submit"
              :disabled="isSubmitting"
              :input-class="['w-52']"
            >
              <span
                v-if="isSubmitting"
                class="w-full h-6 flex items-center justify-center"
              >
                <lottie :options="loadingAnimationOptions" />
              </span>
              <span v-else>
                {{ submitText }}
              </span>
            </FormulateInput>
          </div>
        </div>
      </div>
    </FormulateForm>
  </div>
</template>

<script>
import { defineComponent, computed, watch, ref, onMounted, nextTick } from 'vue'
import { useStore } from '../../../js/store/index.js'
import { useRoute, useRouter } from 'vue-router/composables'
import * as loadingAnimation from '../../../shared/components/loading-dark.json'
import { mapArrayStringsToInt } from '../../../js/util/helper.js'
import { EventBus } from '../../../js/plugins/event-bus.js'
import { EVENT_KEYS } from '../../../js/util/constants.js'

export default defineComponent({
  name: 'FormUpsertConnection',

  props: {
    classes: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()

    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const isSubmitting = ref(false)

    const ACTION_MAP = {
      UPSERT: {
        CONTRIBUTOR: 'contributors/dispatchUpsertConnection',
        MANAGER: 'connections/dispatchUpsertConnection',
        PUBLIC: 'connections/dispatchUpsertConnection',
      },

      UPDATE: {
        CONTRIBUTOR: 'contributors/dispatchUpdateConnection',
        MANAGER: 'connections/dispatchUpdateConnection',
        PUBLIC: 'connections/dispatchUpdateConnection',
      },
    }
    const ROUTE_MAP = {
      SUCCESS: {
        CONTRIBUTOR: { name: 'ContributorsConnections' },
        MANAGER: { name: 'ManagerConnections' },
        PUBLIC: { name: 'Home', params: { 'add-connection': true } },
      },

      EDIT: {
        CONTRIBUTOR: 'ContributorsUpdateConnection',
        MANAGER: 'ManagerUpdateConnection',
        PUBLIC: 'UpdateConnection',
      },

      ADD: {
        CONTRIBUTOR: 'ContributorsAddConnection',
        MANAGER: 'ManagerAddConnection',
        PUBLIC: 'AddConnection',
      },
    }
    const settings = computed(() => store.getters['shared/getSettings'])
    const isAdmin = computed(() => store.getters['shared/getUserAccess'])
    const user = computed(() => store.getters['shared/getUser'])
    const isContributorContext = computed(
      () => store.getters['shared/isContributor'],
    )
    const isManagerContext = computed(() =>
      store.getters['connections/isManagerContext'](route.fullPath),
    )
    const isGeneralContext = computed(
      () =>
        isManagerContext.value ||
        (isContributorContext.value &&
          !isContributorAdminConnectionsOnly.value),
    )
    const context = computed(() => {
      if (isContributorContext.value) {
        return 'CONTRIBUTOR'
      }
      if (isManagerContext.value) {
        return 'MANAGER'
      }
      return 'PUBLIC'
    })

    const isContributorAdminConnectionsOnly = computed(() => {
      return (
        isContributorContext.value &&
        store.getters['shared/isContributorAdminConnectionsOnly']
      )
    })

    const hasConnections = computed(
      () => store.getters['contributors/hasConnections'],
    )
    const connections = computed(
      () => store.getters['contributors/getConnections'],
    )

    const isEditing = ref(false)
    const connectionId = ref(null)

    const wrapperClasses = computed(() => {
      return props.classes || 'bg-white mt-8'
    })

    // Attempt to remove camel/snake case confusion!
    const TAG_SCHOOL_SUPPORT_NAME = 'schoolSupport'

    const connectionTypes = computed(
      () => store.getters['shared/getConnectionsTypesMappedForVueFormulate'],
    )

    const tagsRegions = computed(() => {
      const getter = isContributorContext.value
        ? 'getTagsRegions'
        : 'getTagsRegionsWithoutAllRegions'
      return store.getters['shared/getTagsMappedForVueFormulate'](null, getter)
    })

    const tagsIndustries = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsIndustries',
      ),
    )

    const tagsSchoolSupport = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsSchoolSupport',
      ),
    )

    const tagsPreferences = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsPreferences',
      ),
    )

    const tagsOrganisations = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsOrganisations',
      ),
    )

    const publicContactOptions = [
      { value: 0, label: 'Private' },
      { value: 1, label: 'Public' },
    ]

    const publicSelectValue = computed({
      get: () => (form.value.public ? 1 : 0),
      set: (value) => {
        form.value.public = Number(value) === 1
      },
    })

    const conductText = computed(() => {
      if (isGeneralContext.value) {
        return `Welcome to our Connections service. We're glad to have this connection join
          our growing community supporting career education. Whether this connection
          can spare an hour or a day we appreciate their support. To ensure
          that everyone has a positive and respectful experience, we ask
          that every connection reviews and acknowledges that they will follow and
          adhere to this Code of Conduct.`
      }

      return `Welcome to our Connections service. We're glad to have you join our growing community
        supporting career education and our school. Whether you can spare an hour or a day we appreciate
        your help. To ensure that everyone has a positive and respectful experience, we ask
        that you please review and acknowledge that you will follow and
        adhere to this Code of Conduct.`
    })

    const isTagsCollapsed = ref({
      industries: false,
      schoolSupport: false,
      preferences: false,
      regions: false,
      organisationsSupport: false,
    })

    const form = ref({
      image: null,
      name: '',
      company: '',
      jobTitle: '',
      region: null,
      type: 'PARENT',
      public: false, // @TODO not currently in use. See line ~108 in template for longer description
      publicPhone: false,
      policeChecked: false,
      saveConnection: false,
      email: '',
      linkedIn: '',
      phone: '',
      description: '',
      regions: [],
      industries: [],
      schoolSupport: [],
      preferences: [],
      organisationsSupport: [],
      conduct: false,
    })

    const imageMaskElement = computed(() =>
      document.querySelector(
        '.formulate-input.profile-image .formulate-input-upload-area-mask',
      ),
    )

    const filesElement = computed(() =>
      document.querySelector('.formulate-input.profile-image .formulate-files'),
    )

    const checkboxLabelClasses =
      'transform duration-100 ease-linear left-3 top-1 z-40 flex items-center group'

    const checkboxLabelTooltipClasses =
      'absolute left-full top-0 w-64 z-50 border border-truegray-300 rounded p-4 opacity-0 transition-all duration-300 transform pointer-events-none shadow group-hover:opacity-100 bg-white'

    const checkboxLabelTooltipText = computed(() => {
      return 'Public connections are visible to anyone browsing CareerWise, so you may be contacted directly by a student or parent. Private connections are visible only to school admins.'
    })

    const formLabel = computed(() => {
      return {
        image: isGeneralContext.value
          ? 'Connection profile image (optional)'
          : 'Your profile image (optional)',
        name: isGeneralContext.value
          ? 'Connection full name'
          : 'Your full name',
        company: isGeneralContext.value
          ? 'Connection company (optional)'
          : 'Your company (optional)',
        jobTitle: isContributorAdminConnectionsOnly.value
          ? 'Job title - Company name'
          : isGeneralContext.value
          ? 'Connection job title'
          : 'Job title',
        region:
          isContributorContext.value && !isContributorAdminConnectionsOnly.value
            ? 'Connection region(s)'
            : isGeneralContext.value
            ? 'Which region do they live in?'
            : 'Which region do you live in?',
        type: isGeneralContext.value ? 'Connection type' : 'Type',
        regionPlaceholder: isGeneralContext.value
          ? 'Select region'
          : 'Select your region',
        typePlaceholder: isGeneralContext.value
          ? 'Select type'
          : 'Select your type',
        public: 'Public connection', // @TODO not currently in use. See line ~108 in template for longer description
        policeChecked: 'Police verified',
        saveConnection: 'Save directly to My Connections',
        email: isGeneralContext.value ? 'Connection email' : 'Email',
        linkedIn: isGeneralContext.value
          ? 'Connection LinkedIn (optional)'
          : 'LinkedIn (optional)',
        phone: isGeneralContext.value
          ? 'Connection phone number'
          : 'Phone number',
        description: isGeneralContext.value
          ? 'Provide a brief bio for this connection to be displayed in their profile (up to 150 words).'
          : 'Tell us about yourself and your career to create a bio in your profile (up to 150 words).',
        industries: isGeneralContext.value
          ? `Industries that apply to this connection's career`
          : 'Select all the industries that apply to your career',
        supportTitle:
          isContributorContext.value && !isContributorAdminConnectionsOnly.value
            ? `Other ways this connection would be open to supporting High Schools`
            : isGeneralContext.value
            ? 'Which other ways would this connection like to support'
            : 'Which other ways would you like to support',
        schoolSupport: 'School support',
        preferences:
          isContributorContext.value && !isContributorAdminConnectionsOnly.value
            ? 'How will this connection engage with High School students around career pathways?'
            : 'Career pathway support',
        organisationsSupport: 'Tags that apply to your organisation',
        conduct: isGeneralContext.value
          ? 'This connection has read and agreed to the Code of Conduct'
          : 'I have read and agree to the Code of Conduct',
      }
    })

    const stepLabel = computed(() => {
      return {
        step1: isGeneralContext.value
          ? 'Please add a profile picture and contact details for this connection.'
          : 'Please add your profile picture and contact details.',
        step2: isGeneralContext.value
          ? 'Tell us a little about this connection.'
          : 'Tell us a little about you.',
        step3: isContributorContext.value
          ? 'In addition to providing your contact details so school staff, parents and students can contact you, how else can you engage with High School students around career pathways?'
          : isGeneralContext.value
          ? "In addition to providing this connection's contact details so school staff can get in touch, how else can this connection engage with High School students around career pathways, and how else can they support the school?"
          : 'In addition to providing your contact details so school staff can get in touch, how else can you engage with High School students around career pathways, and how else can you support the school?',
      }
    })

    const submitText = computed(() => {
      // Public
      if (!isGeneralContext.value) {
        return isEditing.value ? 'Update your profile' : 'Sign up'
      }

      // Admin/manager/contributor
      return isEditing.value ? 'Update connection' : 'Add connection'
    })

    const updateImageMaskBeforeContent = (content) => {
      imageMaskElement.value.style.setProperty('--before-content', content)
    }

    const updateImageMaskElement = (element) => {
      imageMaskElement.value.appendChild(element)
      imageMaskElement.value.removeAttribute('style')
      updateImageMaskBeforeContent('"Change image"')
    }

    const getInitialTags = (tags) => {
      return store.getters['shared/getTagsMappedForVueFormulate'](tags).map(
        (tag) => tag.value.toString(),
      )
    }

    const updateInitialTags = (connection) => {
      const KEYS = {
        INDUSTRIES: 'industries',
        SCHOOL_SUPPORT: 'schoolSupport',
        PREFERENCES: 'preferences',
        ORGANISATIONS_SUPPORT: 'organisationsSupport',
        REGIONS: 'regions',
      }
      // @TODO the tags custom input shoud work with props/value but doesn't seem to react,
      // so instead we're using EventBus to update the initial state/selected options
      const initialIndustries = getInitialTags(connection.tags_industries)
      const initialSchoolSupport = getInitialTags(
        connection.tags_school_support,
      )
      const initialPreferences = getInitialTags(connection.tags_preferences)
      const initialOrganisationsSupport = getInitialTags(
        connection.tags_organisations,
      )
      const initialRegions = getInitialTags(connection.tags_regions)

      const KEYS_OTHER = {
        [KEYS.INDUSTRIES]: store.getters['shared/getTagsCategoryKey'](
          KEYS.INDUSTRIES,
        ),

        [KEYS.PREFERENCES]: store.getters['shared/getTagsCategoryKey'](
          KEYS.PREFERENCES,
        ),

        [KEYS.SCHOOL_SUPPORT]: store.getters['shared/getTagsCategoryKey']([
          KEYS.SCHOOL_SUPPORT,
        ]),
      }

      EventBus.$emit(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, {
        type: KEYS.INDUSTRIES,
        form: form.value,
        selectedOptions: initialIndustries,
        other:
          connection.additional_info?.[KEYS_OTHER[KEYS.INDUSTRIES]]?.other ||
          null,
      })
      EventBus.$emit(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, {
        type: TAG_SCHOOL_SUPPORT_NAME,
        form: form.value,
        selectedOptions: initialSchoolSupport,
        other:
          connection.additional_info?.[KEYS_OTHER[KEYS.SCHOOL_SUPPORT]]
            ?.other || null,
      })
      EventBus.$emit(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, {
        type: KEYS.PREFERENCES,
        form: form.value,
        selectedOptions: initialPreferences,
        other:
          connection.additional_info?.[KEYS_OTHER[KEYS.PREFERENCES]]?.other ||
          null,
      })
      EventBus.$emit(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, {
        type: KEYS.ORGANISATIONS_SUPPORT,
        form: form.value,
        selectedOptions: initialOrganisationsSupport,
        other: null,
      })
      EventBus.$emit(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, {
        type: KEYS.REGIONS,
        form: form.value,
        selectedOptions: initialRegions,
        other: null,
      })
    }

    const showModal = (e, type) => {
      if (e) {
        e.preventDefault()
      }

      EventBus.$emit('showModal', type)
    }

    const showExampleProfile = () => {
      const connectionData = {
        connection: {
          id: null,
          name: 'Stuart Dillon-Roberts',
          company: 'Hail',
          job_title: 'CEO',
          region_id: 17,
          type: 'PARENT',
          email: 'stuart@hail.to',
          phone: '123456789',
          public_phone: 0,
          social_linkedIn: null,
          description:
            "I have 35 years of experience working in the IT sector across all facets of technology, including software development, IT support, design, and project management. I left school early and completed a trade apprenticeship. This gave me the opportunity to gain experience in how a business operates and introduced me to the distinct roles in a company. I was drawn to IT and moved into a computer programmer role after my apprenticeship. I then returned to study and completed a part-time Degree and later an MBA. Over time, I held varied roles in technology and by having a broad set of technical skills as well as a degree, my career progressed. Future roles saw me lead large, multinational teams. I was fortunate to spend seven years as an Officer in the Air Force and most recently, I've founded the IT startup businesses, Careerwise and Hail. I am very passionate about technology and am keen to inspire students on tech career choices.",
          image: '/img/connections/images/example-profile-stuart.jpg',
          video: null,
          public_contact: 0,
          approved: 1,
          organisation_id: 1,
          created_at: '2024-03-11T22:38:33.000000Z',
          updated_at: '2024-04-02T21:15:43.000000Z',
          deleted_at: null,
          additional_info: null,
          region: {
            id: 17,
            title: 'Canterbury',
            category: 1,
            description: null,
          },

          local: false,
          disabled: false,
          created_at_ui: '12th Mar, 2024',
          tags_regions: [
            {
              id: 17,
              title: 'Canterbury',
              category: 1,
              description: null,
              pivot: { contact_id: 34, tag_id: 17, tenant_id: 1 },
            },
          ],

          tags_industries: [
            {
              id: 109,
              title: 'Community and Education',
              category: 3,
              description: null,
              pivot: { contact_id: 34, tag_id: 109, tenant_id: 1 },
            },

            {
              id: 110,
              title: 'Engineering, technology and maintenance',
              category: 3,
              description: null,
              pivot: { contact_id: 34, tag_id: 110, tenant_id: 1 },
            },
          ],

          tags_school_support: [
            {
              id: 139,
              title: 'Sponsorship',
              category: 6,
              description: null,
              pivot: { contact_id: 34, tag_id: 139, tenant_id: 1 },
            },
          ],

          tags_preferences: [
            {
              id: 122,
              title: 'In-class speaker',
              category: 5,
              description: null,
              pivot: { contact_id: 34, tag_id: 122, tenant_id: 1 },
            },
            {
              id: 123,
              title: 'Webinar',
              category: 5,
              description: null,
              pivot: { contact_id: 34, tag_id: 123, tenant_id: 1 },
            },
            {
              id: 124,
              title: 'Mentor',
              category: 5,
              description: null,
              pivot: { contact_id: 34, tag_id: 124, tenant_id: 1 },
            },
            {
              id: 127,
              title: 'Work experience',
              category: 5,
              description: null,
              pivot: { contact_id: 34, tag_id: 127, tenant_id: 1 },
            },
          ],

          tags_organisations: [],
          organisation: null,
          policeChecked: true,
          isExampleModal: true,
        },

        example: true,
      }
      EventBus.$emit('showModal', {
        action: 'connection-profile',
        data: {
          connection: connectionData.connection,
        },
      })
    }

    const onSubmit = async (data) => {
      isSubmitting.value = true
      const image = form.value.image?.files[0]?.file || null

      const payload = {}
      const formData = new FormData()

      if (connectionId.value) {
        payload.id = connectionId.value
      }

      // Optional inputs - default to empty string to force set to NULL
      formData.append('image', image || '')
      formData.append('name', data.name)
      formData.append('job_title', data.jobTitle)
      formData.append('company', data.company || '')
      formData.append('type', data.type)
      formData.append('email', data.email)
      formData.append('phone', data.phone)
      formData.append('social_linkedIn', data.linkedIn || '')
      formData.append('description', data.description)
      formData.append('public_contact', data.public) // @TODO not currently in use. See line ~108 in template for longer description
      formData.append('public_phone', data.publicPhone ? 1 : 0)
      formData.append('local_police_checked', data.policeChecked)

      if (!isEditing.value) {
        formData.append('save_connection', data.saveConnection)
      }

      // Region (singular for non-contributor context)
      if (!isContributorContext.value) {
        formData.append('region_id', parseInt(data.region))
      }

      // Tags
      // Note: contributors can select multiple regions, so we use tags rather than the
      // Contact model (regions array will just be empty for non-contributor context).
      const tags = data.industries
        .concat(data.schoolSupport)
        .concat(data.preferences)
        .concat(data.organisationsSupport)
        .concat(data.regions)
      formData.append('tags', mapArrayStringsToInt(tags))

      let additionalInfo = null
      const setAdditionalInfo = (type, data) => {
        const key = store.getters['shared/getTagsCategoryKey'](type)
        if (!additionalInfo) {
          additionalInfo = {}
        }

        additionalInfo[key] = {
          other: data,
        }
      }

      if (data.preferences_other) {
        setAdditionalInfo('preferences', data.preferences_other)
      }

      if (data.industries_other) {
        setAdditionalInfo('industries', data.industries_other)
      }

      if (data.schoolSupport_other) {
        // @todo eventually the input should support details for each
        // for each selected option (each selected school support tag)
        // not just for 'other'
        setAdditionalInfo('school_support', data.schoolSupport_other)
      }

      additionalInfo = additionalInfo ? JSON.stringify(additionalInfo) : ''
      formData.append('additional_info', additionalInfo)

      payload.formData = formData

      const success = await store.dispatch(ACTION_MAP.UPSERT[context.value], {
        ...payload,
        context: context.value,
      })

      isSubmitting.value = false
      if (success) {
        // @TODO - Connections: Watch route properly in Base.vue so we don't ned to hard reload
        if (context.value === 'PUBLIC') {
          window.location.href = `${window.location.origin}?add-connection=true`
        } else {
          // @TODO - Connections: Edit public contact success should be handled differently
          router.push(ROUTE_MAP.SUCCESS[context.value])
        }
      }
    }

    // @TODO implement the equiv for public facing routes when we get there
    const initForm = async () => {
      const routeToAddConnection = () => {
        if (route.name !== ROUTE_MAP.ADD[context.value]) {
          router.push({ name: ROUTE_MAP.ADD[context.value] })
        }
      }

      const handleSelfManagedConnections = (queryId = null) => {
        if (isContributorAdminConnectionsOnly.value) {
          if (hasConnections.value) {
            const selfManagedConnection = connections.value.find(
              (connection) => connection.email === user.value.email,
            )

            if (!selfManagedConnection) {
              // If we couldn't find their connection, assume it doesn't
              // exist yet - route them to the sign up form
              routeToAddConnection()
            } else if (selfManagedConnection.id !== queryId) {
              // Route them to themselves!
              router.push({
                name: ROUTE_MAP.EDIT.CONTRIBUTOR,
                query: { id: selfManagedConnection.id },
              })
            }
          } else {
            // Self-managed connection doesn't exist - route to sign up form
            routeToAddConnection()
          }
        }
      }

      if (
        [ROUTE_MAP.EDIT.CONTRIBUTOR, ROUTE_MAP.EDIT.MANAGER].includes(
          route.name,
        )
      ) {
        const queryId = parseInt(route.query?.id)
        if (queryId) {
          handleSelfManagedConnections(queryId)

          const connection = await store.dispatch(
            ACTION_MAP.UPDATE[context.value],
            { context: context.value, connectionId: queryId },
          )

          if (connection) {
            // Stay on this route and prefill the form
            isEditing.value = true
            connectionId.value = connection.id

            form.value.image = null
            form.value.name = connection.name
            form.value.company = connection.company
            form.value.jobTitle = connection.job_title
            form.value.region = connection.region_id
            form.value.type = connection.type
            form.value.public = !!connection.public_contact // @TODO not currently in use. See line ~108 in template for longer description
            form.value.publicPhone = connection.public_phone
            form.value.policeChecked = !!connection.local_police_checked
            form.value.saveConnection = false
            form.value.email = connection.email
            form.value.linkedIn = connection.social_linkedIn
            form.value.phone = connection.phone
            form.value.description = connection.description
            form.value.conduct = false
            updateInitialTags(connection)

            // Little bit hacky to display an initial image: just
            // add an img element as the direct child of imageMaskElement
            if (imageMaskElement.value && connection.image) {
              const imageElement = document.createElement('img')
              imageElement.src = connection.image
              updateImageMaskElement(imageElement)
            }
          } else {
            // Invalid connectionId: route to add connection instead
            handleSelfManagedConnections()
          }
        } else {
          // Invalid/no connectionId: route to add connection instead
          handleSelfManagedConnections()
        }
      } else if (isContributorAdminConnectionsOnly.value) {
        handleSelfManagedConnections()
      } else {
        isEditing.value = false
        // Init form to default values
        form.value.image = null
        form.value.name = ''
        form.value.company = ''
        form.value.jobTitle = ''
        form.value.region = null
        form.value.type = 'PARENT'
        form.value.public = false // @TODO not currently in use. See line ~108 in template for longer description
        form.value.publicPhone = false
        form.value.policeChecked = false
        form.value.saveConnection = false
        form.value.email = ''
        form.value.linkedIn = ''
        form.value.phone = ''
        form.value.description = ''
        form.value.industries = []
        form.value.schoolSupport = []
        form.value.organisationsSupport = []
        form.value.preferences = []
        form.value.conduct = false

        updateImageMaskBeforeContent('"+ Add image"')
      }

      if (isContributorAdminConnectionsOnly.value && user.value) {
        form.value.name = user.value.name
        form.value.email = user.value.email
      }
    }

    watch(
      () => route.name,
      () => initForm(),
    )

    watch(
      () => form.value.image,
      async () => {
        if (form.value.image?.files[0]) {
          // If we are updating the profile image, remove any
          // initial/existing image (direct img child of imageMaskElement)
          // if it exists, and update to the new selected file
          const initialImageElement =
            imageMaskElement.value.querySelector(':scope > img')
          if (initialImageElement) {
            imageMaskElement.value.removeChild(initialImageElement)
          }

          await nextTick()
          if (imageMaskElement.value && filesElement.value) {
            updateImageMaskElement(filesElement.value)
          }
        }
      },
    )

    onMounted(async () => {
      await initForm()

      EventBus.$on(
        EVENT_KEYS.CO_VUE_FORMULATE_TAGS_TOGGLE_COLLAPSED,
        (type) => {
          const value = isTagsCollapsed.value[type]
          isTagsCollapsed.value[type] = !value
        },
      )
    })

    return {
      route,
      loadingAnimationOptions,
      isSubmitting,
      settings,
      isAdmin,
      isManagerContext,
      isContributorContext,
      isContributorAdminConnectionsOnly,
      isEditing,
      wrapperClasses,
      connectionTypes,
      tagsRegions,
      tagsIndustries,
      tagsSchoolSupport,
      tagsPreferences,
      tagsOrganisations,
      publicContactOptions,
      publicSelectValue,
      conductText,
      isTagsCollapsed,
      form,
      formLabel,
      stepLabel,
      submitText,
      checkboxLabelClasses,
      checkboxLabelTooltipClasses,
      checkboxLabelTooltipText,
      showModal,
      showExampleProfile,
      onSubmit,
    }
  },
})
</script>
